import React from "react";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { Grid, Typography } from "@mui/material";
import Papa from "papaparse";
import {
    getAllFieldNodes,
    getKeyToPathMap,
    getKeyToLabelMap,
    getValueAtKeyPath,
} from "./fieldNodes";
import { renderFieldNodes } from "./renderFieldNodes";
import { downloadCSVFromBlob } from "./utils";

type IsObject<T> = T extends object
    ? T extends any[]
        ? false
        : T extends Function
        ? false
        : true
    : false;

type KeysOf<T> = {
    [K in keyof T & string]: IsObject<T[K]> extends true
        ? `${K}.${KeysOf<T[K]>}`
        : K;
}[keyof T & string];

interface ExportToCSVProps<T> {
    data: T[];
    filename?: string;
    title?: string;
    buttonText?: string;
    selectedKeys: Set<string>;
    onSelectedKeysChange: (keys: Set<string>) => void;
    getLabelForKey?: (key: KeysOf<T>) => string | undefined;
}

/**
 * Generalized component for converting a list of data items to a CSV file.
 * @param props
 * @returns
 */
export function ExportToCSV<T extends object>(props: ExportToCSVProps<T>) {
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);

    const availableFields = React.useMemo(
        () => getAllFieldNodes(props.data),
        [props.data],
    );

    return (
        <React.Fragment>
            <AcxButton color="white" onClick={() => setIsDialogOpen(true)}>
                {props.buttonText || "Export CSV"}
            </AcxButton>
            <AcxDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={props.title || "Export CSV"}
                dialogContentChildren={
                    <Grid container direction="column">
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Select columns to include in the export:
                        </Typography>
                        {renderFieldNodes(
                            availableFields,
                            props.selectedKeys,
                            props.onSelectedKeysChange,
                            0,
                        )}
                    </Grid>
                }
                contentWidth="600px"
                maxWidth="lg"
            >
                <AcxButton
                    color="primary"
                    disabled={props.selectedKeys.size === 0}
                    onClick={() => {
                        const keyToPathMap = getKeyToPathMap(availableFields);
                        const keyToLabelMap = getKeyToLabelMap(availableFields);

                        const filteredData = props.data.map((item) => {
                            const filteredResult: any = {};
                            props.selectedKeys.forEach((dotDelimitedKey) => {
                                const keyPath =
                                    keyToPathMap.get(dotDelimitedKey);
                                const label =
                                    props.getLabelForKey?.(
                                        dotDelimitedKey as KeysOf<T>,
                                    ) ||
                                    keyToLabelMap.get(dotDelimitedKey) ||
                                    dotDelimitedKey;
                                if (keyPath) {
                                    const value = getValueAtKeyPath(
                                        item,
                                        keyPath,
                                    );
                                    if (
                                        (typeof value === "object" ||
                                            Array.isArray(value)) &&
                                        value !== null
                                    ) {
                                        filteredResult[label] =
                                            JSON.stringify(value);
                                    } else {
                                        filteredResult[label] = value;
                                    }
                                }
                            });
                            return filteredResult;
                        });

                        const csv = Papa.unparse(filteredData);
                        const blob = new Blob([csv], {
                            type: "text/csv",
                        });

                        downloadCSVFromBlob(blob, props.filename);

                        setIsDialogOpen(false);
                    }}
                >
                    Download CSV
                </AcxButton>
            </AcxDialog>
        </React.Fragment>
    );
}
